import { z } from "zod";
const CODE_TOOL_TURN_REQUEST_VERSION = 1;
const codeToolSecretNameSchema = z.string().max(64).regex(/^[A-Z][A-Z0-9_]*$/u, {
  message: "Secret names must start with a letter and use capitals, digits and underscores."
});
function buildCodeToolSecretPlaceholder(runId, name) {
  return `{{secret:${runId}:${name}}}`;
}
const placeholderOpen = "(?:\\{|%7[Bb])";
const placeholderClose = "(?:\\}|%7[Dd])";
const placeholderColon = "(?::|%3[Aa])";
function buildCodeToolSecretPlaceholderPattern(runId) {
  return new RegExp(
    `${placeholderOpen}${placeholderOpen}secret${placeholderColon}${runId.replace(/[^a-zA-Z0-9-]/gu, "")}${placeholderColon}([A-Z][A-Z0-9_]*)${placeholderClose}${placeholderClose}`,
    "gu"
  );
}
const codeToolSecretPlaceholderResiduePattern = new RegExp(
  `${placeholderOpen}${placeholderOpen}secret${placeholderColon}`,
  "u"
);
function checkCodeToolSecretPlaceholderRemains(text) {
  return codeToolSecretPlaceholderResiduePattern.test(text);
}
const codeToolEffectKindSchema = z.string().max(40).regex(/^[a-z][a-zA-Z0-9]*$/u, {
  message: "An effect kind is a short camelCase name."
});
const codeToolLimitsSchema = z.object({
  /** The guest's whole WASM linear memory; this is the memory limit. */
  memory_bytes: z.number().int().positive(),
  /** Guest CPU budget for one turn, enforced by the interrupt handler. */
  cpu_ms: z.number().int().positive(),
  /**
   * Effects of each kind for the whole run, counted across turns. Per kind
   * rather than one pool, since what a run can afford of one says nothing
   * about another. A kind the record does not name gets none.
   */
  max_effects: z.record(
    codeToolEffectKindSchema,
    z.number().int().nonnegative()
  ),
  max_result_chars: z.number().int().positive(),
  max_log_entries: z.number().int().nonnegative(),
  max_log_entry_chars: z.number().int().positive()
});
const codeToolRequestMethodSchema = z.enum(["GET", "POST"]);
const codeToolEffectResultSchema = z.union([
  z.object({
    /** Safe to show the script author: never carries a secret value. */
    error: z.string()
  }),
  z.object({ value: z.unknown() })
]);
const codeToolJournalEntrySchema = z.object({
  /** The guest's 0-based effect call order, which replay matches on. */
  seq: z.number().int().nonnegative(),
  kind: codeToolEffectKindSchema,
  /**
   * Over the kind and the input both, so an effect of one kind can never be
   * answered by a result journaled for another.
   */
  hash: z.string(),
  result: codeToolEffectResultSchema
});
const codeToolTurnRequestSchema = z.object({
  version: z.literal(CODE_TOOL_TURN_REQUEST_VERSION),
  /** Fixed for the whole run: every turn must take the same path through the script. */
  run: z.object({
    id: z.uuid(),
    /** What `Date.now()`, `new Date()` and `time.now()` report to the guest. */
    now: z.iso.datetime(),
    seed: z.number().int()
  }),
  code: z.string(),
  /**
   * The run's data, handed to the script as its first argument. Any JSON: the
   * runtime never reads it, so its shape is between the caller and the script.
   * Absent reads as `null`.
   */
  input: z.unknown(),
  /**
   * Names only: the guest gets a placeholder per name, never a value, as
   * `context.secrets` — the second argument, which holds the runtime's own
   * facilities rather than data.
   */
  secret_names: z.array(codeToolSecretNameSchema),
  limits: codeToolLimitsSchema,
  journal: z.array(codeToolJournalEntrySchema)
});
const codeToolLogEntrySchema = z.object({
  level: z.enum(["log", "info", "warn", "error"]),
  message: z.string()
});
const CodeToolErrorCode = {
  /** An uncaught guest error, including a script that does not parse. */
  ScriptError: "script_error",
  /** The script does not `export default` a function. */
  InvalidEntry: "invalid_entry",
  CpuLimit: "cpu_limit",
  MemoryLimit: "memory_limit",
  ResultTooLarge: "result_too_large",
  /** The run's input is larger than one turn request should carry; checked before the first turn. */
  InputTooLarge: "input_too_large",
  /** The returned value has no JSON form (a cycle, a BigInt). */
  ResultNotSerializable: "result_not_serializable",
  /** A replayed effect did not match its journal entry, so the script is not a function of its inputs. */
  NonDeterministic: "non_deterministic",
  /** The entry promise is pending with nothing outstanding to settle it. */
  NeverSettles: "never_settles",
  /** The run's overall deadline passed between turns. */
  Timeout: "timeout",
  /** The caller stopped waiting for the answer. */
  Aborted: "aborted",
  /** A turn response broke the protocol — the runtime is not trusted output. */
  ProtocolViolation: "protocol_violation",
  /** The journaled results no longer fit one invoke payload. */
  JournalTooLarge: "journal_too_large",
  /** The runtime could not read the request: a version it does not speak, or a shape it does not know. */
  UnsupportedRequest: "unsupported_request",
  /** The runtime failed for a reason that is neither the script's nor the caller's. */
  InternalError: "internal_error"
};
const allCodeToolErrorCodes = Object.values(CodeToolErrorCode);
const codeToolErrorSchema = z.object({
  code: z.enum(allCodeToolErrorCodes),
  /** The guest error's constructor name, when the failure came from the script. */
  name: z.string().optional(),
  message: z.string(),
  /** The guest stack, trimmed. Host frames are never included. */
  stack: z.string().optional()
});
const codeToolPendingEffectSchema = z.object({
  seq: z.number().int().nonnegative(),
  kind: codeToolEffectKindSchema,
  hash: z.string(),
  /**
   * The call's arguments as the kind's own schema validated them — so a
   * `fetch` input still carries the secret placeholders the script wrote, and
   * the caller substitutes them on its way out.
   */
  input: z.unknown()
});
const codeToolTurnLogsSchema = z.array(codeToolLogEntrySchema);
const codeToolTurnResponseSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("suspended"),
    pending: z.array(codeToolPendingEffectSchema),
    logs: codeToolTurnLogsSchema
  }),
  z.object({
    status: z.literal("completed"),
    result: z.unknown(),
    logs: codeToolTurnLogsSchema,
    stats: z.object({ cpu_ms: z.number().nonnegative() })
  }),
  z.object({
    status: z.literal("failed"),
    error: codeToolErrorSchema,
    logs: codeToolTurnLogsSchema
  })
]);
export {
  CODE_TOOL_TURN_REQUEST_VERSION,
  CodeToolErrorCode,
  buildCodeToolSecretPlaceholder,
  buildCodeToolSecretPlaceholderPattern,
  checkCodeToolSecretPlaceholderRemains,
  codeToolEffectKindSchema,
  codeToolEffectResultSchema,
  codeToolErrorSchema,
  codeToolJournalEntrySchema,
  codeToolLimitsSchema,
  codeToolLogEntrySchema,
  codeToolPendingEffectSchema,
  codeToolRequestMethodSchema,
  codeToolSecretNameSchema,
  codeToolTurnRequestSchema,
  codeToolTurnResponseSchema
};
//# sourceMappingURL=codeToolTurn.js.map
